/** Lux & billing › the upgrade section: Pro and Business side by side with live plan values (plans API), live
 * prices (billing API), monthly or annual, what each adds over the company's plan, the switch bonus, the Founding
 * offer while it is open, and the cost of a Lux on the plan against buying packs (only when the plan is cheaper).
 * `?upgrade=pro&billing=annual` (the landing page's "Get Pro", through sign-up) preselects and highlights. */
import { ArrowRight, Check, ExternalLink, Gift, Lock, Sparkles, Tag, UserPlus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation, useSearchParams } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { Badge, Button, Segmented, cx, useToast } from "../components/ui";
import { useLuxFormat } from "../components/LuxSpend";
import { useLeaving } from "../components/useOnce";
import type { MessageKey } from "../i18n/keys";
import { DirIcon } from "../i18n/mirror";
import { annualSaving, parseCheckoutIntent, seatPriceText, type Billing, type SelfServePlan } from "../lib/billing";
import { packTotalMinor } from "../lib/lux";
import { afterPercentOff, annualSavingPct, planHighlights, planVsPacks } from "../lib/upgrade";
import { useBilling } from "./BillingPanel";

/** Plan names are never translated. */
const NAME: Record<SelfServePlan, string> = { pro: "Pro", business: "Business" };
const TAGLINE: Record<SelfServePlan, MessageKey> = {
  pro: "lux.upgrade.tagline.pro",
  business: "lux.upgrade.tagline.business",
};

/** Whether Lux & billing shows the upgrade section for this company. */
export function canUpgrade(plan: S["OrganizationPlan"] | undefined): boolean {
  return !!plan && (plan.plan === "free" || plan.plan === "pro");
}

export function UpgradeSection({ orgId, plan, canBuy }: {
  orgId: string;
  plan: S["OrganizationPlan"];
  canBuy: boolean;
}) {
  const toast = useToast();
  const f = useLuxFormat();
  const { t } = f;
  const usd = (c: number) => f.moneyShort(c);
  const billing = useBilling(orgId);
  const [params] = useSearchParams();
  const location = useLocation();
  const ref = useRef<HTMLElement>(null);
  const upgrade = params.get("upgrade");
  const intent = upgrade ? parseCheckoutIntent(`?upgrade=${upgrade}&billing=${params.get("billing") ?? ""}`) : null;
  const [period, setPeriod] = useState<Billing>(intent?.billing ?? "annual");
  const [code, setCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [pending, setPending] = useState<SelfServePlan | null>(null);
  useEffect(() => {
    if ((intent || location.hash === "#upgrade") && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, upgrade, billing.isSuccess]);
  // one Checkout (or portal session) however often a button is clicked, none again until the browser has left
  const checkout = useLeaving({
    mutationFn: (p: SelfServePlan) =>
      unwrap(api.POST("/v1/orgs/{org_id}/billing/checkout", { params: { path: { org_id: orgId } }, body: { plan: p, billing: period, code: code.trim() || null } })),
    url: (r) => r.url,
    onError: (e) => {
      setPending(null);
      toast(errorMessage(e), true);
    },
  });
  const portal = useLeaving({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/billing/portal", { params: { path: { org_id: orgId } } })),
    url: (r) => r.url,
    onError: (e) => toast(errorMessage(e), true),
  });
  // back from a paid Checkout before its webhook has arrived: no second Checkout meanwhile (BillingPanel polls)
  const confirming = params.get("billing") === "success";

  const b = billing.data;
  if (!b || plan.plan === "enterprise" || plan.plan === "business") return null;
  const sub = b.subscription && ["active", "trialing", "past_due"].includes(b.subscription.status) ? b.subscription : null;
  const plans = plan.catalogue?.plans ?? [];
  const valuesOf = (key: string) => (plans.find((p) => p.key === key)?.values ?? {}) as Record<string, unknown>;
  const price = (p: SelfServePlan, per: Billing) => b.prices.find((x) => x.plan === p && x.billing === per);
  const savings = (["pro", "business"] as const).map((p) => {
    const m = price(p, "monthly"), a = price(p, "annual");
    return m && a ? annualSavingPct(m.monthly_cents, a.monthly_cents) : 0;
  });
  const maxSaving = Math.max(0, ...savings);
  const offer = b.offers.find((o) => o.percent_off && o.billings.includes(period));
  const anyOffer = b.offers.find((o) => o.percent_off);
  const isFree = plan.plan === "free";
  const recommended: SelfServePlan = isFree ? "pro" : "business";
  // What a Lux costs the company today in a 50-Lux pack (the plan-against-packs comparison).
  const unit = typeof plan.values.lux_price_cents === "number" ? plan.values.lux_price_cents : null;
  const packPerLux = unit != null ? Math.floor(packTotalMinor(unit, 50, 5) / 50) : null;
  const bonus = (p: SelfServePlan) => {
    const v = valuesOf(p).switch_bonus_designs;
    return typeof v === "number" ? v : 0;
  };
  const switchApproved = plan.switch?.status === "approved";
  const seatDiscount = (p: SelfServePlan) => b.seat_prices.find((s) => s.plan === p && s.billing === "annual")?.annual_discount_pct ?? 0;

  const cta = (p: SelfServePlan) => {
    if (plan.plan === p) return <Button block disabled icon={<Check />}>{t("lux.upgrade.yourPlan")}</Button>;
    const primary = p === recommended;
    if (b.comped)
      return (
        <a className={cx("btn btn-block btn-lg", primary && "btn-primary")} href={`mailto:sales@solaros.app?subject=${encodeURIComponent(t("lux.upgrade.to", { plan: NAME[p] }))}`}>
          {t("lux.upgrade.talk", { plan: NAME[p] })}{" "}
          <DirIcon>
            <ArrowRight size={16} />
          </DirIcon>
        </a>
      );
    if (sub)
      return (
        <Button block size="lg" variant={primary ? "primary" : "default"} icon={<DirIcon><ExternalLink /></DirIcon>} disabled={!canBuy || checkout.busy} loading={portal.busy} onClick={() => portal.run(undefined)}>
          {t("lux.upgrade.to", { plan: NAME[p] })}
        </Button>
      );
    return (
      <Button
        block
        size="lg"
        variant={primary ? "primary" : "default"}
        disabled={!canBuy || !b.available || !price(p, period) || checkout.busy || portal.busy || confirming}
        loading={checkout.busy && pending === p}
        onClick={() => {
          setPending(p);
          checkout.run(p);
        }}
      >
        {t("lux.upgrade.to", { plan: NAME[p] })}{" "}
        <DirIcon>
          <ArrowRight size={16} />
        </DirIcon>
      </Button>
    );
  };

  return (
    <section ref={ref} id="upgrade" className={cx("upgrade", intent && "is-highlighted")} aria-labelledby="upgrade-title">
      <div className="upgrade-head">
        <div>
          <span className="upgrade-eyebrow">
            <Sparkles size={14} /> {isFree ? t("lux.upgrade.eyebrowFree") : t("lux.upgrade.eyebrowPro")}
          </span>
          <h2 id="upgrade-title">{isFree ? t("lux.upgrade.titleFree") : t("lux.upgrade.titlePro")}</h2>
          <p>{isFree ? t("lux.upgrade.introFree") : t("lux.upgrade.introPro")}</p>
        </div>
        <div className="upgrade-period">
          <Segmented<Billing>
            value={period}
            onChange={setPeriod}
            options={[
              { value: "monthly", label: t("lux.billing.monthly") },
              { value: "annual", label: t("lux.billing.annual") },
            ]}
          />
          {maxSaving > 0 && <span className={cx("save-pill", period === "annual" && "on")}>{t("lux.upgrade.save", { off: f.pct(maxSaving) })}</span>}
        </div>
      </div>

      {anyOffer && (
        <div className="upgrade-offer">
          <Gift />
          <div>
            <b>
              {t("lux.upgrade.offer", {
                name: anyOffer.name,
                off: f.pct(anyOffer.percent_off ?? 0),
                plans: new Intl.ListFormat(f.locale, { type: "disjunction" }).format(anyOffer.plans.map((p) => NAME[p as SelfServePlan] ?? p)),
                billing: anyOffer.billings.length === 1 ? anyOffer.billings[0]! : "any",
              })}
            </b>{" "}
            <span>
              <FormattedMessage
                id="lux.upgrade.offerCode"
                values={{
                  code: (c) => <code>{c}</code>,
                  value: anyOffer.code,
                  until: anyOffer.valid_until ? f.date(anyOffer.valid_until) : "none",
                  left: anyOffer.remaining != null ? f.num(anyOffer.remaining) : "none",
                }}
              />
            </span>
          </div>
          {!sub && !b.comped && (
            <Button
              size="sm"
              onClick={() => {
                setCode(anyOffer.code);
                setShowCode(true);
                if (anyOffer.billings.length === 1) setPeriod(anyOffer.billings[0] as Billing);
              }}
            >
              {t("lux.upgrade.apply")}
            </Button>
          )}
        </div>
      )}

      <div className="upgrade-plans">
        {(["pro", "business"] as const).map((p) => {
          const pr = price(p, period);
          const monthly = price(p, "monthly");
          const v = valuesOf(p);
          const lux = typeof v.lux_month === "number" ? v.lux_month : 0;
          const extra = switchApproved ? bonus(p) * 100 : 0;
          const current = plan.plan === p;
          // The company's own plan lists what it has over Free (no "now" values); the others what they add.
          const highlights = current ? planHighlights(valuesOf("free"), v, f).map((h) => ({ ...h, was: null })) : planHighlights(plan.values as Record<string, unknown>, v, f);
          const vs = pr ? planVsPacks(pr.monthly_cents, lux + extra, packPerLux) : null;
          const discounted = offer && offer.plans.includes(p) && pr ? afterPercentOff(pr.monthly_cents, offer.percent_off) : null;
          return (
            <article key={p} className={cx("plan-card", p === recommended && "is-recommended", current && "is-current", intent?.plan === p && "is-intent")}>
              {p === recommended && <span className="plan-ribbon">{t("lux.upgrade.recommended")}</span>}
              <header>
                <h3>{NAME[p]}</h3>
                <p>{t(TAGLINE[p])}</p>
              </header>
              <div className="plan-price">
                <span className="amount">{pr ? usd(pr.monthly_cents) : "—"}</span>
                <span className="per">{t("lux.upgrade.perMonth")}</span>
              </div>
              <div className="plan-billed">
                {period === "annual" && pr && monthly
                  ? t("lux.upgrade.billedYearly", { price: usd(pr.amount_cents), saving: usd(annualSaving(monthly.monthly_cents, pr.monthly_cents)) })
                  : t("lux.upgrade.billedMonthly")}
              </div>
              {discounted != null && offer && (
                <div className="plan-deal">
                  <Tag size={13} /> {t("lux.upgrade.deal", { price: usd(discounted), code: offer.code })}
                </div>
              )}
              {vs && !current && (
                <div className="plan-value">
                  <div>
                    <FormattedMessage id="lux.upgrade.perLux" values={{ b: (c) => <b>{c}</b>, price: f.money(vs.perLuxCents) }} />
                  </div>
                  <div className="muted small">
                    {t("lux.upgrade.vsPacks", { pack: f.money(vs.packPerLuxCents), lux: f.lux(vs.lux), saving: usd(vs.savingCents) })}
                  </div>
                </div>
              )}
              {cta(p)}
              <ul className="plan-features">
                {highlights.length === 0 && lux > 0 && (
                  <li>
                    <Check /> <span>{t("lux.upgrade.hl.luxMonth", { value: f.lux(lux), count: lux, unlimited: "no" })}</span>
                  </li>
                )}
                {highlights.map((h) => (
                  <li key={h.key}>
                    <Check />
                    <span>
                      {h.text}
                      {h.was && <em> {t("lux.upgrade.now", { was: h.was })}</em>}
                    </span>
                  </li>
                ))}
                {pr?.seat_amount_cents ? (
                  <li className="seat-price">
                    <UserPlus />
                    <span>
                      {t("lux.upgrade.seats", { price: seatPriceText(pr.seat_amount_cents, period, usd, seatDiscount(p), true, f) })}
                    </span>
                  </li>
                ) : null}
                {bonus(p) > 0 && (
                  <li className="bonus">
                    <Gift />
                    <span>
                      {switchApproved ? t("lux.upgrade.bonusIncluded", { amount: bonus(p) }) : t("lux.upgrade.bonusOffer", { amount: bonus(p) })}
                    </span>
                  </li>
                )}
              </ul>
            </article>
          );
        })}
      </div>

      <div className="upgrade-foot">
        {!switchApproved && (bonus("pro") > 0 || bonus("business") > 0) && (
          <div className="upgrade-switch">
            <Gift size={18} />
            <span>
              <b>{t("lux.upgrade.switchTitle")}</b> {t("lux.upgrade.switchBody", { pro: bonus("pro"), business: bonus("business") })}{" "}
              <a href="#switch" onClick={(e) => { e.preventDefault(); document.getElementById("switch")?.scrollIntoView({ behavior: "smooth", block: "center" }); }}>
                {t("lux.switch.request.button")}
              </a>
            </span>
          </div>
        )}
        {!sub && !b.comped && (
          <div className="upgrade-trust">
            <span>
              <Lock size={13} />{" "}
              {!b.available ? t("lux.upgrade.unavailable") : canBuy ? t("lux.upgrade.secure") : t("lux.upgrade.noRole")}
            </span>
            {showCode ? (
              <input
                className="input upgrade-code"
                placeholder={t("lux.order.promo")}
                aria-label={t("lux.upgrade.promoAria")}
                maxLength={40}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus={!code}
                dir="auto"
              />
            ) : (
              <button type="button" className="link-btn" onClick={() => setShowCode(true)}>
                {t("lux.upgrade.havePromo")}
              </button>
            )}
          </div>
        )}
        {sub && (
          <div className="upgrade-trust">
            <span>
              <Lock size={13} /> {t("lux.upgrade.subscribed", { plan: NAME[sub.plan as SelfServePlan] ?? sub.plan })}
            </span>
            <Badge tone="ok">{t("lux.upgrade.billing", { billing: sub.billing })}</Badge>
          </div>
        )}
      </div>
    </section>
  );
}
